import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { User, LogOut, Package, Truck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";

const TOKEN_KEY = "ap_customer_token";

const fmtError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const field =
  "w-full rounded-lg border border-white/10 bg-[#141417] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60";

export default function Account() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const headers = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) return;
    api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        return api.get("/account/orders", { headers: { Authorization: `Bearer ${token}` } });
      })
      .then((r) => setOrders(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = mode === "register"
        ? await api.post("/auth/register", { name: form.name, email: form.email, password: form.password })
        : await api.post("/auth/login", { email: form.email, password: form.password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      toast.success(mode === "register" ? "Account created. Welcome to Alpha Peps." : "Welcome back.");
      if (next) navigate(next);
    } catch (e2) {
      setError(fmtError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {}, headers());
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setOrders(null);
  };

  if (!token || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 pt-16" data-testid="account-auth-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass w-full max-w-md rounded-2xl p-8"
        >
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-[#E8E9EB]" />
            <h1 className="font-display text-xl font-bold text-white" data-testid="account-auth-heading">
              {mode === "login" ? "Customer Login" : "Create Account"}
            </h1>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {mode === "login" ? "Access your orders and tracking." : "Track orders, view history, faster checkout."}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-white/10 p-1" data-testid="auth-mode-toggle">
            {[["login", "LOG IN"], ["register", "SIGN UP"]].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => { setMode(k); setError(""); }}
                data-testid={`auth-toggle-${k}`}
                className={`rounded-full py-2 font-mono text-[10px] tracking-[0.2em] transition-colors duration-200 ${
                  mode === k ? "bg-[#E8E9EB] text-[#050811]" : "text-slate-400 hover:text-white"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="auth-form">
            {mode === "register" && (
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="register-name-input" />
            )}
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="auth-email-input" />
            <input required type="password" minLength={6} placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} data-testid="auth-password-input" />
            {error && <p className="text-xs text-red-400" data-testid="auth-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="rounded-full bg-[#6E7683] py-3 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:opacity-50"
            >
              {busy ? "PLEASE WAIT..." : mode === "login" ? "LOG IN" : "CREATE ACCOUNT"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-28 sm:px-8" data-testid="account-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">MY ACCOUNT</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-white" data-testid="account-greeting">
            {user.name}
          </h1>
          <p className="mt-1 font-mono text-xs text-slate-500">{user.email}</p>
        </div>
        <button onClick={logout} data-testid="account-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-slate-300 transition-colors duration-200 hover:border-red-400/50 hover:text-red-300">
          <LogOut className="h-3.5 w-3.5" /> LOG OUT
        </button>
      </div>

      <div className="mt-10">
        <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">ORDER HISTORY</div>
        <div className="mt-5 flex flex-col gap-4" data-testid="order-history">
          {orders === null ? (
            <p className="py-16 text-center font-mono text-xs tracking-[0.3em] text-slate-500">LOADING ORDERS...</p>
          ) : orders.length === 0 ? (
            <div className="glass rounded-xl py-16 text-center" data-testid="order-history-empty">
              <Package className="mx-auto h-8 w-8 text-slate-600" />
              <p className="mt-4 text-sm text-slate-400">No orders yet.</p>
              <Link to="/products" data-testid="history-shop-link" className="group mt-4 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-[#E8E9EB]">
                BROWSE SHOP PRODUCTS <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          ) : (
            orders.map((o) => (
              <Link
                key={o.order_id}
                to={`/order/${o.order_id}`}
                data-testid={`history-order-${o.order_id}`}
                className="glass block rounded-xl p-5 transition-colors duration-200 hover:border-[#E8E9EB]/30"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-white">#{o.order_id}</span>
                    {o.status === "paid" ? (
                      <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-emerald-400">PAID</span>
                    ) : (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-amber-400">AWAITING PAYMENT</span>
                    )}
                    {o.tracking_number && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-slate-300">
                        <Truck className="h-3 w-3" /> {o.tracking_number}
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-sm text-[#E8E9EB]">{money(o.total)}</span>
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.1em] text-slate-500">
                  {o.items.map((i) => `${i.name} x${i.qty}`).join(" / ")} · {new Date(o.created_at).toLocaleDateString("en-AU")}
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
